// The classifier: one motif for one mistake, from the detectors in order,
// most specific first. A forced mate is a mate whatever else the line does;
// a free piece is simply hanging; then the devices that attack through or
// around (discovered attack, fork, pin, skewer); then what the line wins
// without a device of its own (a trapped piece, a defender removed); the
// pawn themes; the attack on the king; and the fallbacks, combination (the
// line gives or wins material by a device not named) and positional (it
// wins nothing).
//
// A blunder is classified on the position after the move, from the
// opponent's best reply; a miss on the position before it, from the
// reader's best line (`lineOf`). A fact with no line is `mate` when it names
// one, else `positional`.
import type { Chess } from '@chess/api-chess';
import type { Motif, MotifInput } from '@chess/api-mistakes';
import { captureDefender } from './capture-defender.ts';
import { combination } from './combination.ts';
import { discoveredAttack } from './discovered.ts';
import { fork } from './fork.ts';
import { hangingPiece } from './hanging.ts';
import { kingAttack } from './king-attack.ts';
import { lineOf, type Detector } from './line.ts';
import { mate } from './mate.ts';
import { pawn } from './pawn.ts';
import { pin } from './pin.ts';
import { skewer } from './skewer.ts';
import { trappedPiece } from './trapped.ts';

/** The detectors, in the order they are asked. */
export const DETECTORS: readonly Detector[] = [mate, hangingPiece, discoveredAttack, fork, pin, skewer, trappedPiece, captureDefender, pawn, kingAttack, combination];

const POSITIONAL: Motif = { family: 'positional' };

export function classifyMotif(input: MotifInput, chess: Chess): Motif {
  const line = lineOf(input, chess);
  if (!line) return input.lost === 'mate' || input.missed === 'mate' ? { family: 'mate', variation: 'other' } : POSITIONAL;
  for (const detect of DETECTORS) {
    try {
      const found = detect(line);
      if (found) return found;
    } catch {
      // A detector that cannot read the position says nothing; the next one is asked.
    }
  }
  return POSITIONAL;
}
